<template>
  <v-sheet outlined rounded="lg" class="pa-4">
    <div class="d-flex justify-space-between align-center mb-4 flex-wrap">
      <v-btn-toggle
        v-if="isAdmin"
        v-model="activityFilter"
        mandatory
        dense
        class="calendar-activity-toggle mb-2 mb-sm-0"
      >
        <v-btn small value="active">Активные</v-btn>
        <v-btn small value="inactive">Неактивные</v-btn>
        <v-btn small value="all">Все</v-btn>
      </v-btn-toggle>
      <v-btn color="#483CB9" dark small class="text-capitalize ml-auto" @click="$emit('add')">
        <v-icon small left>mdi-account-plus</v-icon>
        Эксперт
      </v-btn>
    </div>

    <div v-if="!filteredExperts.length" class="text-caption grey--text text-center py-4">
      Нет экспертов, подходящих под выбранный фильтр
    </div>
    <v-row v-else>
      <v-col v-for="expert in filteredExperts" :key="expert.id" cols="12" sm="6" md="4">
        <v-sheet
          outlined
          rounded="lg"
          class="pa-4 expert-card"
          role="button"
          tabindex="0"
          title="Подробнее об эксперте"
          @click="openDetails(expert.id)"
          @keydown.enter.self="openDetails(expert.id)"
        >
          <div class="d-flex align-center mb-3">
            <v-avatar size="48" color="#483CB9" class="mr-3">
              <img v-if="expert.photo_url" :src="expert.photo_url" :alt="fullName(expert)">
              <span v-else class="white--text text-subtitle-2">{{ initials(fullName(expert)) }}</span>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="font-weight-bold text-body-1">{{ fullName(expert) }}</div>
              <div class="text-caption" style="color:#483CB9;">{{ expert.email }}</div>
              <v-chip x-small class="mt-1 mr-1" :color="expert.role ? '#ece9fb' : '#fdecea'">
                {{ roleLabel(expert.role) }}
              </v-chip>
              <v-chip v-if="!expert.is_active" x-small class="mt-1" color="#f0f0f0">Неактивен</v-chip>
              <div v-if="expert.permanent_conference_url" class="mt-1">
                <a
                  :href="expert.permanent_conference_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-caption d-inline-flex align-center"
                  @click.stop
                >
                  <v-icon size="12" color="#483CB9" class="mr-1">mdi-video-outline</v-icon>
                  Конференция
                </a>
              </div>
            </div>
            <div class="d-flex align-center ml-2">
              <v-btn icon small class="mr-1" @click.stop="$emit('edit', expert)">
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn icon small @click.stop="onDelete(expert.id)">
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>
          <p class="text-body-2 grey--text text--darken-2 mb-3">{{ expert.short_description }}</p>
          <div class="text-caption grey--text mb-3">Открытых интервалов: {{ availabilityCount(expert.id) }}</div>

          <div v-if="expertServices(expert.id).length" class="mb-3 d-flex flex-column align-start">
            <v-chip
              v-for="item in expertServices(expert.id)"
              :key="item.id"
              x-small
              class="mb-1"
              :color="item.is_active === false ? 'grey lighten-2' : '#ece9fb'"
            >
              {{ serviceTypeName(item.service_type) }}, {{ item.max_duration_minutes }} мин
            </v-chip>
          </div>
          <div v-else class="text-caption grey--text mb-3">Услуги не назначены</div>

          <div v-if="showLinks" class="expert-card__links" @click.stop @keydown.enter.stop>
            <v-divider class="mb-2" />
            <div class="d-flex align-center justify-space-between">
              <v-btn text small color="#483CB9" class="px-1 text-none" @click="toggleLinks(expert.id)">
                <v-icon small left>{{ isLinksOpen(expert.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                Ссылки на запись ({{ linksCount(expert.id) }})
              </v-btn>
              <v-btn
                v-if="canManageLinksOf(expert)"
                text
                small
                color="#483CB9"
                class="px-1 text-none"
                @click="$emit('create-link', { kind: 'expert', mainId: expert.id })"
              >
                <v-icon small left>mdi-link-plus</v-icon>
                Ссылка
              </v-btn>
            </div>

            <v-expand-transition>
              <div v-if="isLinksOpen(expert.id)" class="pt-2">
                <div class="text-caption grey--text text--darken-1 mb-1">Личные ссылки</div>
                <div v-if="!ownLinks(expert.id).length" class="text-caption grey--text mb-2">Ссылок пока нет</div>
                <PublicLinkItem
                  v-for="link in ownLinks(expert.id)"
                  :key="`expert-${link.id}`"
                  kind="expert"
                  :link="link"
                  :experts="experts"
                  :services="services"
                  :service-types="serviceTypes"
                  :can-manage="canManageLinksOf(expert)"
                  class="mb-2"
                  @copy="$emit('copy-link', $event)"
                  @edit="$emit('edit-link', $event)"
                  @toggle-active="$emit('toggle-link-active', $event)"
                />

                <div class="text-caption grey--text text--darken-1 mb-1 mt-3">Участвует в ссылках на услуги</div>
                <div v-if="!serviceLinksOf(expert.id).length" class="text-caption grey--text">Не участвует</div>
                <PublicLinkItem
                  v-for="link in serviceLinksOf(expert.id)"
                  :key="`service-${link.id}`"
                  kind="service"
                  :link="link"
                  :experts="experts"
                  :services="services"
                  :service-types="serviceTypes"
                  :caption="link.name ? serviceTypeName(link.service_type) : ''"
                  :show-selection="false"
                  class="mb-2"
                  @copy="$emit('copy-link', $event)"
                />
              </div>
            </v-expand-transition>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <ExpertDetailsDrawer
      v-model="details.show"
      :expert="detailsExpert"
      :experts="experts"
      :services="services"
      :service-types="serviceTypes"
      :availability="availability"
      :appointments="appointments"
      :own-links="detailsExpert ? ownLinks(detailsExpert.id) : []"
      :service-links="detailsExpert ? serviceLinksOf(detailsExpert.id) : []"
      :show-links="showLinks"
      :can-manage-links="Boolean(detailsExpert) && canManageLinksOf(detailsExpert)"
      :is-me="Boolean(detailsExpert) && detailsExpert.id === myExpertId"
      @edit="$emit('edit', $event)"
      @open-date="onDetailsNavigate('open-date', $event)"
      @show-appointments="onDetailsNavigate('show-appointments', $event)"
      @create-link="$emit('create-link', $event)"
      @edit-link="$emit('edit-link', $event)"
      @copy-link="$emit('copy-link', $event)"
      @toggle-link-active="$emit('toggle-link-active', $event)"
    />

    <ConfirmDialog
      v-model="deleteDialog.show"
      title="Удалить эксперта?"
      text="Эксперт будет удалён из календаря."
      confirm-text="Удалить"
      danger
      @confirm="confirmDelete"
    />
  </v-sheet>
</template>

<script>
import ConfirmDialog from './ConfirmDialog.vue';
import PublicLinkItem from './PublicLinkItem.vue';
import ExpertDetailsDrawer from './ExpertDetailsDrawer.vue';
import { isExpertInServiceLink } from './publicLinkLabels';

// Значения поля role эксперта с бэка. У экспертов, заведённых до появления поля, role пустой.
const EXPERT_ROLE_LABELS = {
  manager: 'Менеджер',
  partner: 'Партнёр',
  corp_client: 'Корп. клиент',
};

export default {
  name: 'ExpertsList',
  components: { ConfirmDialog, PublicLinkItem, ExpertDetailsDrawer },
  props: {
    experts: { type: Array, default: () => [] },
    availability: { type: Array, default: () => [] }, // открытые интервалы (Availability, status === 'open')
    services: { type: Array, default: () => [] },
    serviceTypes: { type: Array, default: () => [] },
    appointments: { type: Array, default: () => [] },
    expertLinks: { type: Array, default: () => [] },
    serviceLinks: { type: Array, default: () => [] },
    showLinks: { type: Boolean, default: false }, // false — нет прав на ссылки (403)
    // Админ управляет ссылками всех экспертов и видит неактивных; эксперт календаря —
    // только своими ссылками и только активных экспертов.
    isAdmin: { type: Boolean, default: false },
    myExpertId: { type: Number, default: null },
  },
  data() {
    return {
      activityFilter: 'active', // active | inactive | all
      deleteDialog: { show: false, id: null },
      openLinksIds: [], // карточки с раскрытым блоком ссылок
      details: { show: false, id: null }, // боковая панель с подробностями эксперта
    };
  },
  computed: {
    detailsExpert() {
      return this.experts.find((expert) => expert.id === this.details.id) || null;
    },
    // Не админу фильтр не показываем — только активные эксперты.
    filteredExperts() {
      return this.experts.filter((expert) => {
        const isActive = expert.is_active !== false;
        if (!this.isAdmin) return isActive;
        if (this.activityFilter === 'active') return isActive;
        if (this.activityFilter === 'inactive') return !isActive;
        return true;
      });
    },
  },
  methods: {
    openDetails(id) {
      this.details = { show: true, id };
    },
    // Переход из панели в другую вкладку — панель закрываем.
    onDetailsNavigate(event, payload) {
      this.details.show = false;
      this.$emit(event, payload);
    },
    fullName(expert) {
      return `${expert.first_name || ''} ${expert.last_name || ''}`.trim();
    },
    roleLabel(role) {
      return EXPERT_ROLE_LABELS[role] || 'Роль не указана';
    },
    availabilityCount(expertId) {
      return this.availability.filter((item) => item.expert === expertId).length;
    },
    expertServices(expertId) {
      return this.services.filter((item) => item.expert === expertId && item.is_active !== false);
    },
    serviceTypeName(id) {
      const serviceType = this.serviceTypes.find((item) => item.id === id);
      return serviceType ? serviceType.name : '';
    },
    canManageLinksOf(expert) {
      return this.isAdmin || expert.id === this.myExpertId;
    },
    ownLinks(expertId) {
      return this.expertLinks.filter((link) => link.expert === expertId);
    },
    // Отключённые ссылки на услуги эксперту ничего не дают — показываем только активные.
    serviceLinksOf(expertId) {
      return this.serviceLinks.filter((link) => link.is_active
        && isExpertInServiceLink(this.experts, this.services, expertId, link));
    },
    linksCount(expertId) {
      return this.ownLinks(expertId).length + this.serviceLinksOf(expertId).length;
    },
    isLinksOpen(expertId) {
      return this.openLinksIds.includes(expertId);
    },
    toggleLinks(expertId) {
      this.openLinksIds = this.isLinksOpen(expertId)
        ? this.openLinksIds.filter((id) => id !== expertId)
        : [...this.openLinksIds, expertId];
    },
    initials(name) {
      return name.split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    onDelete(id) {
      this.deleteDialog = { show: true, id };
    },
    confirmDelete() {
      this.$emit('delete', this.deleteDialog.id);
      this.deleteDialog.show = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.expert-card {
  height: 100%;
  cursor: pointer;
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &:hover, &:focus-visible {
    border-color: #483CB9 !important;
    box-shadow: 0 4px 16px rgba(72, 60, 185, 0.08);
    outline: none;
  }
}

.expert-card__links {
  cursor: default;
}

.calendar-activity-toggle .v-btn {
  text-transform: none;
}
</style>
